package com.downdogapp.client.views

import com.downdogapp.*
import com.downdogapp.BoxSizing.BORDER_BOX
import com.downdogapp.Color.Companion
import com.downdogapp.FontWeight.MEDIUM
import com.downdogapp.FontWeight.REGULAR
import com.downdogapp.JustifyContent.SPACE_BETWEEN
import com.downdogapp.Position.ABSOLUTE
import com.downdogapp.Position.RELATIVE
import com.downdogapp.Size.Companion.percent
import com.downdogapp.Size.Companion.px
import com.downdogapp.Size.Companion.vh
import com.downdogapp.TextAlign.CENTER
import com.downdogapp.client.*
import com.downdogapp.client.controllers.SelectorViewController
import com.downdogapp.client.controllers.playback.PlaybackViewController
import com.downdogapp.client.resources.Images
import com.downdogapp.client.singleton.App
import com.downdogapp.client.views.playback.SongAttributionView
import com.downdogapp.client.views.playback.songAttributionView
import com.downdogapp.client.views.start.StartView
import com.downdogapp.client.widget.*
import com.downdogapp.client.widget.CustomRow.Companion.customRow
import kotlinx.dom.clear
import org.w3c.dom.*

actual class SelectorView actual constructor(
  private val controller: SelectorViewController
) : TopLevelView(), View {

  private var table: TableView
  private var groups: HTMLDivElement
  private var selectedRow: TableRow? = null
  private var attributionView: SongAttributionView?
  private var selectButton: TextButton
  private var headerContainer: HTMLDivElement
  private var titleLabel: HTMLSpanElement
  private var subtitleLabel: HTMLSpanElement
  private var backgroundImageView: HTMLImageElement?
  private var backgroundVideoView: HTMLDivElement?

  override val root =
    controllerView {
      if (controller.hasVideoOptions || controller.hasImageOptions) {
        bgColor = Companion.BLACK
      } else {
        background(true)
      }

      fg =
        foreground(hasSidebar = App.getViewController(PlaybackViewController::class) == null && !controller.isOnboarding) {
          verticalFlexBox(horizontalAlign = AlignItems.CENTER) {
            position = RELATIVE
            if (controller.isOnboarding) {
              width = percent(100)
            }

            backgroundImageView =
              if (controller.hasImageOptions)
                img {
                  (this as HTMLElement).height = vh(100)
                  (this as HTMLElement).width = percent(100)
                  objectFit = ObjectFit.CONTAIN
                }
              else
                null

            backgroundVideoView =
              if (controller.hasVideoOptions)
                horizontalFlexBox(horizontalJustify = SPACE_BETWEEN) {
                  position = ABSOLUTE
                  height = vh(100)
                  zIndex = ZIndex.zVal(20)
                }
              else
                null

            horizontalFlexBox(horizontalJustify = JustifyContent.CENTER) {
              position = ABSOLUTE
              height = vh(100)
              width = percent(100)
              zIndex = ZIndex.zVal(22)

              verticalFlexBox {
                height = vh(100)
                width = px(560)
                paddingBottom = percent(4)
                boxSizing = BORDER_BOX

                headerContainer = horizontalFlexBox(horizontalJustify = SPACE_BETWEEN) {
                  marginTop = px(15)
                  width = percent(100)

                  imageButton(
                    image = Images.SETTINGS_LEFT_ARROW,
                    clickFn = controller::onBackClicked
                  ) {
                    marginTop = px(5)
                  }

                  if (controller.isOnboarding) {
                    horizontalFlexBox {
                      marginTop = px(20)
                      for (i in 0..<controller.numOnboardingScreens!!) {
                        val image = if (i == controller.onboardingScreenIndex!!)
                          Images.ONBOARDING_PROGRESS_ACTIVE
                        else
                          Images.ONBOARDING_PROGRESS_INACTIVE

                        imageView(image) {
                          marginHorizontal = px(3)
                        }
                      }
                    }

                    textButton(
                      text = Strings.SKIP,
                      fontSize = 20,
                      fontWeight = MEDIUM,
                      clickFn = controller::onSkipClicked
                    )
                  }
                }

                verticalFlexBox(verticalJustify = JustifyContent.CENTER) {
                  marginTop = StartView.TITLE_TOP_MARGIN_PX - if (controller.isOnboarding) px(40) else px(0)
                  paddingBottom = px(18)

                  titleLabel = label(text = controller.title, fontSize = 24, fontWeight = REGULAR, textAlign = CENTER)

                  subtitleLabel =
                    label(text = controller.subtitle, fontSize = 15, fontWeight = REGULAR, textAlign = CENTER) {
                      marginTop = px(8)
                      marginBottom = px(12)
                    }
                }

                attributionView = if (controller.options.any { it.hasSongAttribution })
                  songAttributionView().apply {
                    root.height = px(50)
                    root.flexShrink = 0.0
                    root.marginHorizontal = HORIZONTAL_CONTENT_GUTTER_PX
                    onSkipClicked(controller::onSkippedSong)
                  }
                else
                  null

                table = tableView(::getContents, maskBottomGradient = controller.options.size > 7)

                groups = verticalFlexBox {
                  controller.groupLabels?.forEach { label ->
                    horizontalFlexBox(horizontalJustify = JustifyContent.CENTER, verticalAlign = AlignItems.CENTER) {
                      width = percent(100)
                      boxSizing = BORDER_BOX
                      height = px(60)
                      label(
                        text = label,
                        fontSize = 22,
                        fontWeight = MEDIUM,
                        textAlign = CENTER
                      )
                      onClick {
                        controller.onGroupLabelClicked(label)
                      }
                    }
                  }
                }

                selectButton = roundedTextButton(
                  text = Strings.SELECT,
                  marginHorizontal = HORIZONTAL_CONTENT_GUTTER_PX,
                  clickFn = controller::onSelectClicked
                ) {
                  marginTop = px(8)
                }
              }
            }
          }
        }
    }

  init {
    refreshView()

    controller.typeId?.let {
      MediaSampleManager.getImage(typeId = it, optionId = controller.selectedId)?.let {
        updateBackgroundImage(image = it)
      }
      MediaSampleManager.getVideoPlayer(typeId = it, optionId = controller.selectedId)?.let {
        backgroundVideoUpdated(player = it)
      }
    }
  }

  override fun onBecameVisible() {
    selectedRow?.root?.scrollToCenter()
  }

  actual override fun refreshView() {
    if (controller.displayingGroups) {
      table.root.hide()
      selectButton.hide()
      attributionView?.hide()
      groups.show()
    } else {
      table.root.show()
      table.refreshContents()
      selectButton.show()
      attributionView?.show()
      groups.hide()
    }
    if (controller.groupLabels == null || controller.displayingGroups) {
      subtitleLabel.show()
      titleLabel.text = controller.title
    } else {
      subtitleLabel.hide()
      titleLabel.text = controller.visibleOptions.first().groupLabel
    }
    if (controller.isOnboarding || (controller.groupLabels != null && !controller.displayingGroups)) {
      headerContainer.show()
    } else {
      headerContainer.hide()
    }
  }

  actual fun setSongAttribution(text: String?) {
    attributionView?.setText(text)
  }

  actual fun backgroundVideoUpdated(player: MediaPlayer?) {
    if (controller.hasVideoOptions) {
      backgroundVideoView?.apply {
        clear()
        bgColor = if (player == null) Color.CLEAR else Color.BLACK
        (player as? JsMediaPlayer)?.apply {
          addToView(backgroundVideoView!!)
          applyToElement {
            position = RELATIVE
            height = percent(100)
            width = percent(100)
          }

          show()
        }
      }
    }
  }

  actual fun updateVideoFrame() {}

  actual fun updateBackgroundImage(image: ServerImage?) {
    backgroundImageView?.src = (image as? JsServerImage)?.url ?: ""
  }

  private fun getContents() = TableContents(
    controller.visibleOptions
      .map { option ->
        customRow(null) {
          val isSelected = option.id == controller.selectedId
          val showAudioIcon = isSelected && option.hasAudioSample && controller.audioIcon != null

          root.apply {
            paddingVertical = px(6)
            paddingHorizontal = HORIZONTAL_CONTENT_GUTTER_PX
            boxSizing = BORDER_BOX

            horizontalFlexBox(horizontalJustify = JustifyContent.CENTER, verticalAlign = AlignItems.CENTER) {
              width = percent(100)
              borderRadius = px(8)
              paddingHorizontal = HORIZONTAL_CONTENT_GUTTER_PX
              boxSizing = BORDER_BOX

              if (isSelected) {
                selectedRow = this@customRow // Used to scroll to this row in onBecameVisible()
                bgColor = Color.white(0.9)
              } else {
                bgColor = Color.grey(0.15)
              }

              verticalFlexBox(horizontalAlign = AlignItems.CENTER) {
                marginVertical = px(24)

                horizontalFlexBox(horizontalJustify = JustifyContent.CENTER, verticalAlign = AlignItems.CENTER) {
                  if (showAudioIcon) {
                    div {
                      width = px(50)
                      marginLeft = px(-20)
                    }
                  }

                  label(
                    text = option.label,
                    fontSize = 15,
                    fontWeight = MEDIUM,
                    textAlign = CENTER
                  ) {
                    if (isSelected) {
                      txtColor = Color.BLACK
                    }
                  }

                  if (showAudioIcon) {
                    imageView(controller.audioIcon!!) {
                      marginVertical = px(-20)
                      marginRight = px(-20)
                    }
                  }
                }

                option.helpText?.let {
                  label(it, fontSize = 14, textAlign = CENTER) {
                    marginTop = px(10)
                    if (isSelected) {
                      txtColor = Color.black(0.6)
                    }
                  }
                }
              }

              onClick {
                controller.onItemClicked(option.id)
              }
            }
          }
        }
      }
  )
}
